import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ImageIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
    setCompanyName,
    setFont,
    setInitialTheme,
    setLanguage,
    setLogo,
    setPrimaryColor,
    setSecondaryColor,
    setTextSecondaryColor,
    setUnitDisplay,
    setCornerRadius,
    setOrderButtonLabel
} from '../../../redux/vendor/settings/settingsActions';
import { useAutosave } from '../../../common/hooks/use-autosave.hook';
import { VendorState } from '../../../redux/vendor/rootReducer';
import { useSettingsApi } from '../../../common/hooks/use-settings-api.hook';
import {
    DEFAULT_GROUT_SETTINGS,
    GroutSettings,
    GroutSettingsGroup,
    groutConfigFields,
    groutSettingsFromConfig,
} from '../../../modules/vendor/settings/components/GroutSettingsGroup';
import {
    SettingsColorField,
    SettingsField,
    SettingsGroup,
    SettingsToggleRow,
} from '../../../components/design-system/common/SettingsPanel';
import { normalizeDisplayUnit } from '../../../common/utils/units';

/** The vendor's brand colour when they have not picked one — the same default the MUI palette falls back to. */
const FALLBACK_BRAND_COLOR = '#003ca9';

/**
 * The Brand section of the Visualizer settings editor: logo, name, language,
 * font, colours, the order button and a few display switches.
 *
 * Only rendered inside the studio's settings column, which supplies the title,
 * the save status and the live canvas. Every change is dispatched to Redux as
 * it happens so the canvas follows, and saved by itself a moment later.
 * `inTab` is still accepted so the call site keeps compiling, but there is no
 * standalone layout any more.
 */
/**
 * The vendor's general settings, as two sections of the Visualizer studio.
 *
 * `brand` is how the visualizer looks: identity, colours, font and corners.
 * `shopping` is how it works for a shopper: whether it is on, what the order
 * button does, the language and units, and the privacy policy. They were one
 * long form, where a colour sat next to a privacy link.
 *
 * One component for both, and the studio keeps the same instance mounted
 * between them, so switching sections never interrupts an edit on its way to
 * being saved.
 */
export const GeneralSettings = ({ part = 'brand' }: { inTab?: boolean; part?: 'brand' | 'shopping' }) => {
    const { t, i18n } = useTranslation();
    const { theme, initialTheme } = useSelector((state: VendorState) => state.settings);
    const initialThemeRef = useRef(initialTheme);
    useEffect(() => { initialThemeRef.current = initialTheme; }, [initialTheme]);

    const [orderButtonLabel, setLocalorderButtonLabel] = useState(theme?.orderButtonLabel || "Order");
    const [orderButtonAction, setLocalorderButtonAction] = useState(theme?.orderButtonAction || "link");
    // Seeded empty, not from `theme` — the Theme slice doesn't carry it. getConfig() fills it.
    const [cartPageUrl, setCartPageUrl] = useState('')
    // Linked from the visualizer's consent notices. The vendor is the controller for
    // their own storefront's visitors, so their policy is the right one to show.
    const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState('');
    // Grout v2: shoppers change the grout of the products that allow it.
    const [grout, setGrout] = useState<GroutSettings>(DEFAULT_GROUT_SETTINGS);

    const [textSecondaryColor, setLocalTextSecondaryColor] = useState(theme?.textSecondaryColor)
    const [primaryColor, setLocalPrimaryColor] = useState(theme?.primaryColor)
    const [secondaryColor, setLocalSecondaryColor] = useState(theme?.secondaryColor)

    const [companyName, setLocalCompanyName] = useState(theme?.vendorName || '');
    const [companyLogo, setLocalCompanyLogo] = useState(theme?.logo || '');
    const [companyLanguage, setLocalCompanyLanguage] = useState(theme?.language || 'en');
    const [preferredFont, setLocalPreferredFont] = useState(theme?.font || '');
    const [unitDisplay, setLocalUnitDisplay] = useState<string>(normalizeDisplayUnit(theme?.unitDisplay));
    const [isVisualizerEnabled, setIsVisualizerEnabled] = useState(true);
    const [showSizeInProductName, setShowSizeInProductName] = useState(false);
    const [cornerRadius, setLocalCornerRadius] = useState('8');
    /** Set once the stored config has loaded: nothing is saved before that. */
    const [loaded, setLoaded] = useState(false);

    const [showDisableVisualizerDialog, setShowDisableVisualizerDialog] = useState(false);

    const [fonts, setFonts] = useState<any>([])

    const logoInputRef = useRef(null)

    const settingsAPI = useSettingsApi()
    const dispatch = useDispatch()

    // The slider's filled track shows the vendor's own secondary colour,
    // which is what the MUI palette used to hand back.
    const brandColor = secondaryColor || FALLBACK_BRAND_COLOR;

    useEffect(() => {
        const getFonts = async () => {
            setFonts(settingsAPI.fonts)
        }
        const getConfig = async () => {
            const config = await settingsAPI.findConfig()
            if (config) {
                setLocalUnitDisplay(normalizeDisplayUnit(config.unitDisplay))
                setIsVisualizerEnabled(config.isVisualizerEnabled !== false) // Default to true if not set
                setShowSizeInProductName(config.showSizeInProductName === true) // Default to false if not set
                const loadedCornerRadius = (config.cornerRadius ?? '8px').replace('px', '')
                setLocalCornerRadius(loadedCornerRadius)
                dispatch(setCornerRadius(loadedCornerRadius))
                setCartPageUrl(config.cartPageUrl || '')
                setPrivacyPolicyUrl(config.privacyPolicyUrl || '')
                setGrout(groutSettingsFromConfig(config))
                setLoaded(true)
            }
        }
        getFonts()
        getConfig().catch((error) => console.error('Error loading settings:', error))
        // No reverting the theme on the way out: edits are saved, not previews
        // waiting for a Save, and the one still pending is saved as this unmounts.
    }, [])

    useEffect(() => {
        if (theme) {
            setLocalCompanyName(theme.vendorName || '');
            setLocalCompanyLogo(theme.logo || '');
            setLocalCompanyLanguage(theme.language || 'en');
            setLocalPreferredFont(theme.font || '');
            setLocalorderButtonLabel(theme.orderButtonLabel || 'Order');
            setLocalorderButtonAction(theme.orderButtonAction || 'link');
            // cartPageUrl and privacyPolicyUrl are deliberately NOT restored from `theme`.
            // The Theme slice does not carry them, so `theme.x` is always undefined and
            // this effect would blank whatever getConfig() had just loaded from the API —
            // and it re-runs on any theme change, including the setCornerRadius dispatch
            // inside getConfig itself. That is why a saved URL vanished on reload.
            // getConfig is the single source for these two fields.
            setLocalUnitDisplay(normalizeDisplayUnit(theme.unitDisplay));
            setLocalTextSecondaryColor(theme.textSecondaryColor || '#000000');
            setLocalPrimaryColor(theme.primaryColor || '#1976d2');
            setLocalSecondaryColor(theme.secondaryColor || '#dc004e');
        }
    }, [theme]);

    const setSelectedFont = (selectedFont) => {
        setLocalPreferredFont(selectedFont)
        dispatch(setFont(selectedFont))
    }

    /*
     * Colours apply as they are picked. They used to wait behind a
     * Cancel/Validate pair; the canvas beside the column now previews them
     * live, and nothing is stored until Save, so the extra step bought nothing.
     */
    const validateTextSecondaryColor = (color: string) => {
        setLocalTextSecondaryColor(color)
        dispatch(setTextSecondaryColor(color))
    }

    const validatePrimaryColor = (color: string) => {
        setLocalPrimaryColor(color)
        dispatch(setPrimaryColor(color))
    }

    const validateSecondaryColor = (color: string) => {
        setLocalSecondaryColor(color)
        dispatch(setSecondaryColor(color))
    }

    const handleLanguageChange = (lng: string) => {
        setLocalCompanyLanguage(lng);
        dispatch(setLanguage(lng));
        i18n.changeLanguage(lng);
    };

    const config = {
        language: [companyLanguage],
        logo: companyLogo,
        company: companyName,
        primaryColor: primaryColor,
        secondaryColor: secondaryColor,
        textSecondaryColor: textSecondaryColor,
        orderButtonLabel: orderButtonLabel,
        orderButtonAction: orderButtonAction,
        cartPageUrl: cartPageUrl || undefined,
        privacyPolicyUrl: privacyPolicyUrl || undefined,
        unitDisplay: unitDisplay,
        isVisualizerEnabled: isVisualizerEnabled,
        showSizeInProductName: showSizeInProductName,
        cornerRadius: `${cornerRadius}px`,
        ...groutConfigFields(grout),
    }

    // Saved a moment after the last change. A name and a button label are
    // required, so a field cleared on the way to a new value is not saved empty.
    const { flush } = useAutosave({
        data: config,
        ready: loaded && !!companyName.trim() && !!orderButtonLabel.trim(),
        save: async () => {
            await settingsAPI.updateConfig(config);
            dispatch(setInitialTheme());
        },
    });

    const pickImageHandler = () => {
        logoInputRef.current.click();
    };

    const handleCompanyLogoChange = async (event) => {
        const file = event.target.files[0];

        if (!file) return;

        try {
            // Upload to S3 and get CDN URL
            const response = await settingsAPI.uploadLogo(file);
            const logoUrl = response.logoUrl;

            // Update local state and Redux with CDN URL
            setLocalCompanyLogo(logoUrl);
            dispatch(setLogo(logoUrl));

            // Show success message
            console.log('Logo uploaded successfully:', logoUrl);
        } catch (error) {
            console.error('Failed to upload logo:', error);
            // Optionally show error message to user
            alert('Failed to upload logo. Please try again.');
        }
    };

    const handleorderButtonLabelChange = (event) => {
        setLocalorderButtonLabel(event.target.value);
        dispatch(setOrderButtonLabel(event.target.value));
    };

    const handleorderButtonActionChange = (value: string) => {
        setLocalorderButtonAction(value);
    };

    const handleUnitDisplayChange = (value: string) => {
        setLocalUnitDisplay(value);
        dispatch(setUnitDisplay(value));
    };

    const handleVisualizerToggle = (checked: boolean) => {
        // If turning off, show confirmation dialog
        if (!checked) {
            setShowDisableVisualizerDialog(true);
        } else {
            // If turning on, just enable it
            setIsVisualizerEnabled(true);
        }
    };

    const handleConfirmDisableVisualizer = () => {
        setIsVisualizerEnabled(false);
        setShowDisableVisualizerDialog(false);
    };

    const handleCancelDisableVisualizer = () => {
        setShowDisableVisualizerDialog(false);
    };

    const handleShowSizeInProductNameToggle = (checked: boolean) => {
        setShowSizeInProductName(checked);
    };

    return (
        <>
            <form onSubmit={(e) => { e.preventDefault(); void flush(); }} className="flex flex-col gap-8">
                {part === 'brand' && (<>
                {/* Identity */}
                <SettingsGroup title={t('PAGES.SETTINGS.GROUPS.IDENTITY', 'Identity')}>
                    <div className="flex items-end gap-3">
                        <SettingsField label={t('PAGES.SETTINGS.FIELDS.LOGO')} htmlFor="brand-logo">
                            <button
                                id="brand-logo"
                                type="button"
                                onClick={pickImageHandler}
                                aria-label={t('PAGES.SETTINGS.FIELDS.LOGO')}
                                className="flex size-16 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-lg border border-dashed border-input bg-muted/40 transition-colors hover:bg-muted"
                            >
                                {companyLogo ? (
                                    <img src={companyLogo} alt={companyName} className="block size-full object-contain" />
                                ) : (
                                    <ImageIcon className="size-5 text-muted-foreground" />
                                )}
                            </button>
                        </SettingsField>
                        <input
                            type="file"
                            ref={logoInputRef}
                            name="image"
                            accept="image/*"
                            onChange={handleCompanyLogoChange}
                            className="hidden"
                        />

                        <SettingsField
                            label={t('PAGES.SETTINGS.FIELDS.COMPANY_NAME')}
                            htmlFor="brand-company-name"
                            className="min-w-0 flex-1"
                        >
                            <Input
                                id="brand-company-name"
                                value={companyName}
                                onChange={(e) => {
                                    setLocalCompanyName(e.target.value);
                                    dispatch(setCompanyName(e.target.value));
                                }}
                                required
                            />
                        </SettingsField>
                    </div>

                </SettingsGroup>

                {/* Colours */}
                <SettingsGroup title={t('PAGES.SETTINGS.COLORS')}>
                    <div className="grid grid-cols-2 gap-3">
                        <SettingsColorField
                            id="brand-primary-color"
                            label={t('PAGES.SETTINGS.FIELDS.PRIMARY_COLOR')}
                            value={primaryColor}
                            onChange={validatePrimaryColor}
                        />
                        <SettingsColorField
                            id="brand-secondary-color"
                            label={t('PAGES.SETTINGS.FIELDS.SECONDARY_COLOR')}
                            value={secondaryColor}
                            onChange={validateSecondaryColor}
                        />
                    </div>
                    <SettingsColorField
                        id="brand-text-secondary-color"
                        label={t('PAGES.SETTINGS.FIELDS.TEXT_SECONDARY_COLOR')}
                        value={textSecondaryColor}
                        onChange={validateTextSecondaryColor}
                    />
                </SettingsGroup>

                {/* Style */}
                <SettingsGroup title={t('PAGES.SETTINGS.GROUPS.STYLE', 'Style')}>
                    <SettingsField label={t('PAGES.SETTINGS.FIELDS.PREFERRED_FONT')}>
                        {/* Each option is set in the font it names — that is the point of the list. */}
                        <Select value={preferredFont || undefined} onValueChange={setSelectedFont}>
                            <SelectTrigger className="w-full">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {fonts?.map((font, index) => (
                                    <SelectItem value={font} key={index}>
                                        <span style={{ fontFamily: font }}>{font}</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </SettingsField>

                    <SettingsField
                        label={t('PAGES.SETTINGS.FIELDS.BUTTON_BORDER_RADIUS')}
                        info={t('PAGES.SETTINGS.FIELDS.BUTTON_BORDER_RADIUS_HELP')}
                    >
                        <div className="flex items-center gap-3">
                            <Slider
                                min={0}
                                max={32}
                                step={1}
                                value={[parseInt(cornerRadius) || 0]}
                                onValueChange={(next) => {
                                    const value = String(next[0]);
                                    setLocalCornerRadius(value);
                                    dispatch(setCornerRadius(value));
                                }}
                                className="flex-1 [&_[data-slot=slider-range]]:bg-[var(--brand)]"
                                style={{ ['--brand' as any]: brandColor }}
                            />
                            <span className="min-w-10 text-end text-sm tabular-nums text-muted-foreground">
                                {cornerRadius}px
                            </span>
                        </div>
                    </SettingsField>
                </SettingsGroup>

                </>)}

                {part === 'shopping' && (<>
                {/* Availability */}
                <SettingsGroup title={t('PAGES.SETTINGS.GROUPS.AVAILABILITY', 'Availability')}>
                    <SettingsToggleRow
                        id="brand-visualizer-enabled"
                        label={t('PAGES.SETTINGS.FIELDS.VISUALIZER_ENABLED')}
                        description={isVisualizerEnabled ? t('PAGES.SETTINGS.FIELDS.VISUALIZER_ENABLED_YES') : t('PAGES.SETTINGS.FIELDS.VISUALIZER_ENABLED_NO')}
                        checked={isVisualizerEnabled}
                        onCheckedChange={handleVisualizerToggle}
                    />
                </SettingsGroup>

                {/* Ordering */}
                <SettingsGroup
                    title={t('PAGES.SETTINGS.ORDER_BUTTON', 'Order button')}
                    description={t('PAGES.SETTINGS.GROUPS.ORDERING_DESC', 'The button on each product in the visualizer, and where it takes the shopper.')}
                >
                    <SettingsField label={t('PAGES.SETTINGS.FIELDS.BUTTON_LABEL')} htmlFor="brand-order-label">
                        <Input id="brand-order-label" value={orderButtonLabel} onChange={handleorderButtonLabelChange} required />
                    </SettingsField>

                    <SettingsField label={t('PAGES.SETTINGS.FIELDS.BUTTON_ACTION')}>
                        <ToggleGroup
                            type="single"
                            variant="outline"
                            size="sm"
                            value={orderButtonAction}
                            onValueChange={(v) => v && handleorderButtonActionChange(v)}
                        >
                            <ToggleGroupItem value="link">{t('PAGES.SETTINGS.FIELDS.BUTTON_ACTION_LINK')}</ToggleGroupItem>
                            <ToggleGroupItem value="addToCartLink">{t('PAGES.SETTINGS.FIELDS.BUTTON_ACTION_ADD_TO_CART')}</ToggleGroupItem>
                        </ToggleGroup>
                    </SettingsField>

                    {orderButtonAction === 'addToCartLink' && (
                        <SettingsField
                            label={t('PAGES.SETTINGS.FIELDS.CART_PAGE_URL')}
                            htmlFor="brand-cart-url"
                            hint={t('PAGES.SETTINGS.FIELDS.CART_PAGE_URL_HELPER')}
                        >
                            <Input
                                id="brand-cart-url"
                                value={cartPageUrl}
                                onChange={(e) => setCartPageUrl(e.target.value)}
                                placeholder="https://example.com/cart"
                            />
                        </SettingsField>
                    )}
                </SettingsGroup>

                {/* Language and units */}
                <SettingsGroup
                    title={t('PAGES.SETTINGS.GROUPS.LANGUAGE_UNITS', 'Language and units')}
                    description={t('PAGES.SETTINGS.GROUPS.LANGUAGE_UNITS_DESC', 'How the visualizer speaks to shoppers and writes sizes.')}
                >
                    <SettingsField label={t('PAGES.SETTINGS.FIELDS.PREFERRED_LANGUAGES')}>
                        <Select value={companyLanguage} onValueChange={handleLanguageChange}>
                            <SelectTrigger className="w-full">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="fr">🇫🇷 Français</SelectItem>
                                <SelectItem value="en">🇬🇧 English</SelectItem>
                                <SelectItem value="es">🇪🇸 Español</SelectItem>
                                <SelectItem value="ar">🇸🇦 العربية</SelectItem>
                            </SelectContent>
                        </Select>
                    </SettingsField>

                    <SettingsField
                        label={t('PAGES.SETTINGS.FIELDS.UNIT_DISPLAY')}
                        hint={t('PAGES.SETTINGS.FIELDS.UNIT_DISPLAY_LABEL')}
                    >
                        <ToggleGroup
                            type="single"
                            variant="outline"
                            size="sm"
                            value={unitDisplay}
                            onValueChange={(v) => v && handleUnitDisplayChange(v)}
                        >
                            <ToggleGroupItem value="cm">{t('PAGES.SETTINGS.FIELDS.UNIT_CM', 'Centimetres (cm)')}</ToggleGroupItem>
                            <ToggleGroupItem value="mm">{t('PAGES.SETTINGS.FIELDS.UNIT_MM', 'Millimetres (mm)')}</ToggleGroupItem>
                            <ToggleGroupItem value="in">{t('PAGES.SETTINGS.FIELDS.UNIT_IN', 'Inches (in)')}</ToggleGroupItem>
                        </ToggleGroup>
                    </SettingsField>

                    <SettingsToggleRow
                        id="brand-show-size"
                        label={t('PAGES.SETTINGS.FIELDS.SHOW_SIZE_IN_PRODUCT_NAME')}
                        description={showSizeInProductName ? t('PAGES.SETTINGS.FIELDS.SHOW_SIZE_IN_PRODUCT_NAME_YES') : t('PAGES.SETTINGS.FIELDS.SHOW_SIZE_IN_PRODUCT_NAME_NO')}
                        checked={showSizeInProductName}
                        onCheckedChange={handleShowSizeInProductNameToggle}
                    />
                </SettingsGroup>

                {/* Grout */}
                <GroutSettingsGroup value={grout} onChange={setGrout} />

                {/* Privacy */}
                <SettingsGroup title={t('PAGES.SETTINGS.GROUPS.PRIVACY', 'Privacy')}>
                    <SettingsField
                        label={t('PAGES.SETTINGS.FIELDS.PRIVACY_POLICY_URL')}
                        htmlFor="brand-privacy-url"
                        hint={t('PAGES.SETTINGS.FIELDS.PRIVACY_POLICY_URL_HELPER')}
                    >
                        <Input
                            id="brand-privacy-url"
                            value={privacyPolicyUrl}
                            onChange={(e) => setPrivacyPolicyUrl(e.target.value)}
                            placeholder="https://example.com/privacy"
                        />
                    </SettingsField>
                </SettingsGroup>
                </>)}
            </form>

            {/* Confirmation Dialog for Disabling Visualizer */}
            <Dialog open={showDisableVisualizerDialog} onOpenChange={(open) => { if (!open) handleCancelDisableVisualizer(); }}>
                <DialogContent className="sm:max-w-[400px]">
                    <DialogHeader>
                        <DialogTitle>{t('PAGES.SETTINGS.DISABLE_VISUALIZER_TITLE')}</DialogTitle>
                        <DialogDescription>{t('PAGES.SETTINGS.DISABLE_VISUALIZER_MESSAGE')}</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button type="button" variant="outline" className="h-9" onClick={handleCancelDisableVisualizer}>
                            {t('COMMON.CANCEL')}
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            className="h-9 border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive"
                            onClick={handleConfirmDisableVisualizer}
                        >
                            {t('PAGES.SETTINGS.DISABLE_VISUALIZER_CONFIRM')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};
